import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Accordion, AccordionButton, AccordionIcon, AccordionItem, AccordionPanel, Box, Collapse, Text } from '@chakra-ui/react';

import Logo from './layout/Logo';
import { SocialCircles } from './layout/AnnouncementBar';
import useCatalog from '../hooks/useCatalog';
import { STORE, whatsappLink } from '../config/store';

const PAYMENTS = ['UPI', 'VISA', 'Mastercard', 'RuPay'];

// Short brand copy above the footer with a Read more expander (design-system.md §3.4)
const AboutBlock = () => {
  const [open, setOpen] = useState(false);
  return (
    <Box px={{ base: '16px', md: '40px' }} py={{ base: '16px', md: '24px' }} >
      <Box border='1px solid' borderColor='raano.border' borderRadius='8px' p={{ base: '14px', md: '18px' }} >
        <Text as='h2' fontSize='14px' fontWeight={500} >Raano – The Men's Club: menswear for work, weekends and celebrations</Text>
        <Collapse in={open} animateOpacity >
          <Text fontSize='14px' lineHeight='21px' color='raano.muted' mt='10px' >
            Raano is a men's clothing store in Bhuj, Gujarat. We stock shirts, tees, jeans, jackets, suits and blazers picked for fit and fabric first, so they last past the season.
            Shop online with free delivery and 30-day returns, or visit the store to try the fit in person. Questions about sizing? Message us on WhatsApp and we'll help you choose.
          </Text>
        </Collapse>
        <Text as='button' fontSize='14px' fontWeight={500} mt='8px' textDecoration='underline' onClick={() => setOpen(o => !o)} >{open ? 'Read less' : 'Read more'}</Text>
      </Box>
    </Box>
  );
};

const Footer = () => {

  const navigate = useNavigate();
  const { categories } = useCatalog();

  const columns = [
    {
      title: 'Shop',
      links: [
        ...categories.map(c => ({ label: c.name, onClick: () => navigate('/search', { state: { categoryId: c._id } }) })),
        { label: 'Shop all', onClick: () => navigate('/search', { state: {} }) },
      ]
    },
    {
      title: 'Company',
      links: [
        { label: 'About us', to: '/pages/about' },
        { label: 'Visit our store', href: STORE.mapsUrl },
        { label: 'Instagram', href: STORE.instagramUrl },
      ]
    },
    {
      title: 'Support',
      links: [
        { label: 'Contact us', to: '/pages/contact' },
        { label: 'FAQs', to: '/pages/faqs' },
        { label: 'WhatsApp ' + STORE.whatsappDisplay, href: whatsappLink('Hi Raano!') },
        { label: 'Shipping & returns', to: '/policies/refund-policy' },
        { label: 'Privacy policy', to: '/policies/privacy-policy' },
      ]
    },
  ];

  const FooterLink = ({ label, to, href, onClick }) => (
    <Text
      as={href ? 'a' : 'button'} href={href} target={href ? '_blank' : undefined} rel={href ? 'noreferrer' : undefined}
      display='block' textAlign='left' fontSize='14px' lineHeight='22px' py='3px' color='whiteAlpha.800' _hover={{ color: 'raano.white' }}
      onClick={onClick || (to ? () => navigate(to) : undefined)}
    >{label}</Text>
  );

  return (
    <Box as='footer' >
      <AboutBlock />
      <Box bg='raano.black' color='raano.white' pb={{ base: '80px', md: 0 }} >
        <Box display={{ base: 'block', md: 'flex' }} px={{ base: '16px', md: '40px' }} pt={{ base: '32px', md: '64px' }} pb={{ base: '8px', md: '48px' }} gap='40px' >
          <Box width={{ md: '380px' }} flexShrink={0} >
            <Logo light size={{ base: '40px', md: '48px' }} />
            <Text fontSize='12px' lineHeight='18px' color='whiteAlpha.800' mt='16px' maxWidth='320px' >
              <b>{STORE.name}</b>. Menswear for work, weekends and celebrations, picked for fit and fabric. Free delivery and 30-day returns.
            </Text>
            <Box mt='18px' mb={{ base: '24px', md: 0 }} ><SocialCircles size={32} /></Box>
          </Box>

          {/* Desktop columns */}
          <Box display={{ base: 'none', md: 'flex' }} flex={1} justifyContent='space-between' maxWidth='760px' ml='auto' >
            {columns.map(col => (
              <Box key={col.title} minWidth='150px' >
                <Text textStyle='footerHeading' textTransform='uppercase' mb='14px' >{col.title}</Text>
                {col.links.map(l => <FooterLink key={l.label} {...l} />)}
              </Box>
            ))}
          </Box>

          {/* Phone accordions */}
          <Accordion display={{ base: 'block', md: 'none' }} allowToggle >
            {columns.map(col => (
              <AccordionItem key={col.title} borderColor='whiteAlpha.300' >
                <AccordionButton px={0} py='12px' _hover={{ bg: 'transparent' }} >
                  <Text flex={1} textAlign='left' textStyle='footerHeading' textTransform='uppercase' >{col.title}</Text>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel px={0} pt={0} pb='12px' >
                  {col.links.map(l => <FooterLink key={l.label} {...l} />)}
                </AccordionPanel>
              </AccordionItem>
            ))}
          </Accordion>
        </Box>

        <Box display='flex' flexDirection={{ base: 'column-reverse', md: 'row' }} alignItems={{ base: 'flex-start', md: 'center' }} justifyContent='space-between' gap='12px' px={{ base: '16px', md: '40px' }} py='18px' borderTop='1px solid' borderColor='whiteAlpha.300' >
          <Text fontSize='12px' color='whiteAlpha.700' >
            © {new Date().getFullYear()} {STORE.name} · Made by <a href='https://divyanshujangid.in' rel='noreferrer' target='_blank' ><i>Divyanshu Jangid</i></a> · <a href='/image-credits.html' target='_blank' rel='noreferrer' >Image credits</a>
          </Text>
          <Box display='flex' gap='8px' >
            {PAYMENTS.map(p => <Box key={p} px='8px' height='24px' display='flex' alignItems='center' borderRadius='4px' bg='raano.white' color='raano.black' fontSize='11px' fontWeight={700} sx={{ fontStretch: '75%' }} >{p}</Box>)}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Footer;
